import { getPetitionerParty, getRespondentParty, roundTo } from "@/calcs/utils";
import { CaseData, DocumentsModel, StepDownModel, YearInfoModel } from "@/types/case";
import { SummaryModel } from "@/calcs/documents/summary";
import { calcWorksheet1Party, calcWorksheet1Section4, calcWorksheet1Section3, calcWorksheet1Section5 } from "./worksheet1Calcs";
import { Worksheet1Model } from "@/calcs/documents/worksheet1";
import { calcSummaryParty } from "./summaryCalcs";
import { calcHighIncomeEstimator } from "../../2026/IA/highIncomeEstimatorCalcs";
import { taxRules2025 } from "../taxRules";
import { debugLog } from "@/calcs/calcDebug";


export function calcDocumentsIA2025(caseData: CaseData, processStepDowns: boolean, oddIfAlternating: boolean): void {

    // When split custody but splits missing, derive from existing splitCases so we can run the split block and recompute stepDownCases.
    if (caseData.children.custodyArrangement === 'split' && (caseData.children.splits?.length ?? 0) === 0 && (caseData.documents?.splitCases?.length ?? 0) > 0) {
        const derived: { custodyArrangement?: string; overnightsPerYear?: string }[] = [];
        for (const sc of caseData.documents!.splitCases!) {
            const arr = sc.children?.custodyArrangement;
            const count = sc.children?.count ?? 0;
            for (let i = 0; i < count; i++) {
                derived.push({ custodyArrangement: arr, overnightsPerYear: sc.children?.overnightsPerYear as string | undefined });
            }
        }
        caseData.children = { ...caseData.children, splits: derived };
    }

    // When split custody, only splitCases are used; main case is not calculated.
    if (caseData.children.custodyArrangement === 'split' && (caseData.children.splits?.length ?? 0) > 0) {
        const splitCases = calcSplitCases(caseData);
        caseData.documents = {
            summary: undefined,
            worksheet1: undefined,
            highIncomeEstimator: undefined,
            stepDownCases: [],
            splitCases,
            alternatingEvenCase: undefined,
        };
        for (const splitCase of splitCases) {
            splitCase.documents.stepDownCases = calcStepDownCasesForSplitCase(splitCase, caseData);
            calcDocumentsIA2025(splitCase, false, oddIfAlternating);
            for (const stepDownCase of splitCase.documents.stepDownCases ?? []) {
                calcDocumentsIA2025(stepDownCase, false, oddIfAlternating);
            }
            populateSection6AFromStepDownCases(splitCase);
            if (caseData.children.taxSchedule === 'alternating' && oddIfAlternating) {
                splitCase.documents.alternatingEvenCase = calcEvenCase(splitCase);
            }
        }
        // Phase 2: main-case summary and stepDownCases from split/stepdown results.
        calcMainCaseSummaryFromSplits(caseData, splitCases);
        calcMainCaseStepDownCasesFromSplits(caseData, splitCases);
        return;
    }

    const petitioner = getPetitionerParty(caseData);
    const respondent = getRespondentParty(caseData);

    const petitionerFirstName = petitioner?.personal?.firstName ?? '';
    const respondentFirstName = respondent?.personal?.firstName ?? '';


    const petitionerWorksheet1 = calcWorksheet1Party(caseData, oddIfAlternating, petitioner, taxRules2025);
    const respondentWorksheet1 = calcWorksheet1Party(caseData, oddIfAlternating, respondent, taxRules2025);
    const section3 = calcWorksheet1Section3(caseData, petitionerWorksheet1, respondentWorksheet1);
    const section4 = calcWorksheet1Section4(caseData, petitionerWorksheet1, respondentWorksheet1);
    const section5 = calcWorksheet1Section5(caseData);

    // calc values for worksheet1 pdf
    const worksheet1: Worksheet1Model = {
        petitioner: petitionerWorksheet1,
        respondent: respondentWorksheet1,
        section3: section3,
        section4: section4,
        section5: section5,
        section6: {
            section6A: [],
            section6B: [],
        },
    };

    let finalChildSupport = 0.0;
    let finalCashMedicalSupport = 0.0;
    let finalPayor = '';
    let finalPayee = '';


    if (caseData?.children?.custodyArrangement === 'shared') {
        if ((section4?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) {
            finalChildSupport = section4?.petitioner?.finalChildSupportAmount ?? 0.0;
            finalCashMedicalSupport = section4?.petitioner?.finalCashMedicalSupportAmount ?? 0.0;
            finalPayor = petitionerFirstName;
            finalPayee = respondentFirstName;
        } else if ((section4?.respondent?.finalChildSupportAmount ?? 0.0) > 0.0) {
            finalChildSupport = section4?.respondent?.finalChildSupportAmount ?? 0.0;
            finalCashMedicalSupport = section4?.respondent?.finalCashMedicalSupportAmount ?? 0.0;
            finalPayor = respondentFirstName;
            finalPayee = petitionerFirstName;
        }
    } else {
        if ((section3?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) {
            finalChildSupport = section3?.petitioner?.finalChildSupportAmount ?? 0.0;
            finalCashMedicalSupport = section3?.petitioner?.finalCashMedicalSupportAmount ?? 0.0;
            finalPayor = petitionerFirstName;
            finalPayee = respondentFirstName;
        } else if ((section3?.respondent?.finalChildSupportAmount ?? 0.0) > 0.0) {
            finalChildSupport = section3?.respondent?.finalChildSupportAmount ?? 0.0;
            finalCashMedicalSupport = section3?.respondent?.finalCashMedicalSupportAmount ?? 0.0;
            finalPayor = respondentFirstName;
            finalPayee = petitionerFirstName;
        }
    }


    // calc values for summary pdf
    const summary: SummaryModel = {
        petitioner: calcSummaryParty(caseData, petitioner, petitionerWorksheet1),
        respondent: calcSummaryParty(caseData, respondent, respondentWorksheet1),
        finalChildSupport: finalChildSupport,
        finalCashMedicalSupport: finalCashMedicalSupport,
        finalPayor: finalPayor,
        finalPayee: finalPayee,
    };


    const stateCode = caseData.basic?.stateCode ?? 'IA';
    const supportLevels = taxRules2025.states[stateCode]?.support?.childSupport;
    const capIncome = supportLevels?.length ? supportLevels.reduce((m, l) => Math.max(m, l.combinedAdjustedNetIncomeMaximum ?? 0), 0) : 0;
    const highIncomeEstimator = (supportLevels?.length && capIncome > 0)
        ? (calcHighIncomeEstimator(caseData, worksheet1, supportLevels, capIncome) ?? undefined)
        : undefined;

    const isSplitOrStepDown = caseData.caseType === 'splitCase' || caseData.caseType === 'stepDownCase';
    caseData.documents = {
        summary: summary,
        worksheet1: worksheet1,
        highIncomeEstimator,
        stepDownCases: isSplitOrStepDown ? (caseData.documents?.stepDownCases ?? []) : [],
        splitCases: isSplitOrStepDown ? (caseData.documents?.splitCases ?? []) : [],
        alternatingEvenCase: undefined
    };




    if (caseData.children.taxSchedule === 'alternating' && oddIfAlternating) {
        // this does alternating tax years.
        // we just calculated the odd years.
        // clone ourselves and calculate teh even years, storing that in the documents.alternatingEvenCase property.
        // note this sets the final payor and payee to the offset payor and payee.
        caseData.documents.alternatingEvenCase = calcEvenCase(caseData);


    }

    if (processStepDowns) {
        // now we need to calculate each step down, which can have its own 
        caseData.documents.stepDownCases = calcStepDowns(caseData);
    }
}

/** Phase 2: compute main case documents.summary from split case summaries only (no stepDownCases). Ultimate payor = higher total; same payor add, different subtract. */
function calcMainCaseSummaryFromSplits(caseData: CaseData, splitCases: CaseData[]): void {
    const petitioner = getPetitionerParty(caseData);
    const respondent = getRespondentParty(caseData);
    const petitionerFirstName = petitioner?.personal?.firstName ?? '';
    const respondentFirstName = respondent?.personal?.firstName ?? '';
    // First pass: child support totals by payor from split summaries only (no stepDownCases); ultimate payor = higher total CS.
    let totalCsPet = 0;
    let totalCsResp = 0;
    for (const splitCase of splitCases) {
        const s = splitCase.documents?.summary;
        if (!s) continue;
        const amt = s.finalChildSupport ?? 0;
        const payor = s.finalPayor;
        if (payor === petitionerFirstName) {
            totalCsPet += amt;
        } else if (payor === respondentFirstName) {
            totalCsResp += amt;
        }
    }
    const ultimatePayor = totalCsPet >= totalCsResp ? petitionerFirstName : respondentFirstName;
    // Second pass: net from split summaries only; add if payor matches ultimate, else subtract (same ultimate for CS and medical).
    let netChildSupport = 0;
    let netCashMedical = 0;
    for (const splitCase of splitCases) {
        const s = splitCase.documents?.summary;
        if (!s) continue;
        const amt = s.finalChildSupport ?? 0;
        const cash = s.finalCashMedicalSupport ?? 0;
        if (s.finalPayor === ultimatePayor) {
            netChildSupport += amt;
            netCashMedical += cash;
        } else {
            netChildSupport -= amt;
            netCashMedical -= cash;
        }
    }
    const finalChildSupport = roundTo(Math.abs(netChildSupport), 2);
    const finalCashMedicalSupport = roundTo(Math.abs(netCashMedical), 2);
    const firstSummary = splitCases[0]?.documents?.summary;
    const finalPayor = (finalChildSupport === 0 && finalCashMedicalSupport === 0)
        ? (firstSummary?.finalPayor ?? petitionerFirstName)
        : ultimatePayor;
    const finalPayee = finalPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName;
    caseData.documents.summary = {
        petitioner: { reasonableCashMedicalSupportAmount: firstSummary?.petitioner?.reasonableCashMedicalSupportAmount ?? 0 },
        respondent: { reasonableCashMedicalSupportAmount: firstSummary?.respondent?.reasonableCashMedicalSupportAmount ?? 0 },
        finalChildSupport,
        finalCashMedicalSupport,
        finalPayor,
        finalPayee,
    };
}

/** When n total children remain (oldest age out), returns per-arrangement child counts from the last n entries in splits (oldest first). If splits is null/empty, derives from splitCases order and counts. */
function getStepDownCountsPerArrangement(
    splits: { custodyArrangement?: string }[] | null,
    splitCases: CaseData[],
    n: number,
    totalChildren: number
): Record<string, number> {
    const result: Record<string, number> = {};
    if (n <= 0) return result;
    if (splits != null && splits.length > 0) {
        const take = Math.min(n, splits.length);
        const start = splits.length - take;
        for (let i = start; i < splits.length; i++) {
            const arr = splits[i]?.custodyArrangement ?? '';
            result[arr] = (result[arr] ?? 0) + 1;
        }
        return result;
    }
    const rangeStart = totalChildren - n;
    const rangeEnd = totalChildren;
    let indexStart = 0;
    for (const splitCase of splitCases) {
        const arrangement = splitCase?.children?.custodyArrangement ?? '';
        const count = splitCase?.children?.count ?? 0;
        const indexEnd = indexStart + count;
        const overlapStart = Math.max(indexStart, rangeStart);
        const overlapEnd = Math.min(indexEnd, rangeEnd);
        const atLevel = Math.max(0, overlapEnd - overlapStart);
        if (atLevel > 0) {
            result[arrangement] = (result[arrangement] ?? 0) + atLevel;
        }
        indexStart = indexEnd;
    }
    return result;
}

/** Returns the stepdown case for the given total level n, or the split case itself for n == totalChildren. Match by stepDownTotalLevel. */
function getSplitOrStepDownCase(splitCase: CaseData, targetTotalLevel: number): CaseData | null {
    const stepDownCases = splitCase.documents?.stepDownCases ?? [];
    for (const sd of stepDownCases) {
        if (sd.stepDownTotalLevel === targetTotalLevel) return sd;
    }
    return null;
}

/** Phase 2: aggregate step-down amounts per stage (n = total children remaining). For each n, collect the worksheet from each split that represents that arrangement's share of the last n children; then offset. Keyed by n, display descending. */
function calcMainCaseStepDownCasesFromSplits(caseData: CaseData, splitCases: CaseData[]): void {
    const petitioner = getPetitionerParty(caseData);
    const respondent = getRespondentParty(caseData);
    const petitionerFirstName = petitioner?.personal?.firstName ?? '';
    const respondentFirstName = respondent?.personal?.firstName ?? '';
    const totalChildren = caseData.children?.count ?? 0;
    const splits = caseData.children?.splits ?? null;
    const byChildCount = new Map<number, CaseData>();
    for (let n = totalChildren - 1; n >= 1; n--) {
        const countsPerArrangement = getStepDownCountsPerArrangement(splits, splitCases, n, totalChildren);
        const byN: CaseData[] = [];
        for (const splitCase of splitCases) {
            const arrangement = splitCase.children?.custodyArrangement ?? '';
            const count = countsPerArrangement[arrangement] ?? 0;
            if (count <= 0) continue;
            const caseForStage = getSplitOrStepDownCase(splitCase, n);
            if (caseForStage?.documents?.summary != null) byN.push(caseForStage);
        }
        if (byN.length === 0) continue;
        let petSum = 0;
        let respSum = 0;
        let petMed = 0;
        let respMed = 0;
        for (const c of byN) {
            const sum = c.documents?.summary;
            const amt = sum?.finalChildSupport ?? 0;
            const cash = sum?.finalCashMedicalSupport ?? 0;
            const payor = sum?.finalPayor;
            if (payor === petitionerFirstName) {
                petSum += amt;
                petMed += cash;
            } else if (payor === respondentFirstName) {
                respSum += amt;
                respMed += cash;
            }
        }
        const samePayor = (petSum > 0 && respSum === 0) || (respSum > 0 && petSum === 0);
        const aggCs = samePayor ? petSum + respSum : Math.abs(petSum - respSum);
        const aggMed = samePayor ? petMed + respMed : Math.abs(petMed - respMed);
        const aggPayor = samePayor ? (petSum > 0 ? petitionerFirstName : respondentFirstName) : (petSum >= respSum ? petitionerFirstName : respondentFirstName);
        const aggPayee = aggPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName;
        const template = byN[0];
        const aggregated = structuredClone(template);
        if (!aggregated.documents) aggregated.documents = {} as CaseData['documents'];
        const templateSummary = template.documents?.summary;
        aggregated.documents.summary = {
            petitioner: templateSummary?.petitioner ?? { reasonableCashMedicalSupportAmount: 0 },
            respondent: templateSummary?.respondent ?? { reasonableCashMedicalSupportAmount: 0 },
            finalChildSupport: roundTo(aggCs, 2),
            finalCashMedicalSupport: roundTo(aggMed, 2),
            finalPayor: aggPayor,
            finalPayee: aggPayee,
        };
        aggregated.children = { ...aggregated.children, count: n };

        // Set Worksheet1 claim counts from root Step Down for this n so Worksheet PDF override shows correct "claims X children"
        const stepDownA = getStepDownEntryForCount(caseData.parties?.partyA ?? null, n);
        const stepDownB = getStepDownEntryForCount(caseData.parties?.partyB ?? null, n);
        const petitioner0to16 = (stepDownA?.all?.age5Under ?? 0) + (stepDownA?.all?.age6to16 ?? 0);
        const petitioner17Plus = stepDownA?.all?.age17Over ?? 0;
        const respondent0to16 = (stepDownB?.all?.age5Under ?? 0) + (stepDownB?.all?.age6to16 ?? 0);
        const respondent17Plus = stepDownB?.all?.age17Over ?? 0;
        if (aggregated.documents?.worksheet1) {
            const w1 = aggregated.documents.worksheet1;
            w1.petitioner = w1.petitioner ?? ({} as typeof w1.petitioner);
            w1.petitioner!.netMonthlyIncomeSection = w1.petitioner!.netMonthlyIncomeSection ?? ({} as typeof w1.petitioner.netMonthlyIncomeSection);
            w1.petitioner!.netMonthlyIncomeSection!.children0to16 = petitioner0to16;
            w1.petitioner!.netMonthlyIncomeSection!.children17Plus = petitioner17Plus;
            w1.respondent = w1.respondent ?? ({} as typeof w1.respondent);
            w1.respondent!.netMonthlyIncomeSection = w1.respondent!.netMonthlyIncomeSection ?? ({} as typeof w1.respondent.netMonthlyIncomeSection);
            w1.respondent!.netMonthlyIncomeSection!.children0to16 = respondent0to16;
            w1.respondent!.netMonthlyIncomeSection!.children17Plus = respondent17Plus;
        }

        // Set party claim schedules from root Step Down for this n so Summary PDF "Children Claimed" is correct
        if (!aggregated.parties) aggregated.parties = {} as CaseData['parties'];
        const partyA = aggregated.parties.partyA ?? ({} as unknown as NonNullable<CaseData['parties']>['partyA']);
        const partyB = aggregated.parties.partyB ?? ({} as unknown as NonNullable<CaseData['parties']>['partyB']);
        aggregated.parties.partyA = partyA;
        aggregated.parties.partyB = partyB;
        if (!partyA!.tax) partyA!.tax = {} as unknown as NonNullable<NonNullable<CaseData['parties']>['partyA']>['tax'];
        if (!partyB!.tax) partyB!.tax = {} as unknown as NonNullable<NonNullable<CaseData['parties']>['partyB']>['tax'];
        partyA!.tax!.claimSchedule = { all: stepDownA?.all, even: stepDownA?.even, odd: stepDownA?.odd };
        partyB!.tax!.claimSchedule = { all: stepDownB?.all, even: stepDownB?.even, odd: stepDownB?.odd };

        byChildCount.set(n, aggregated);
    }
    // Build array by child count only: for each n in descending order, take the case for that n. Display order is separate from match-by-count.
    const descendingCounts = Array.from(byChildCount.keys()).sort((a, b) => b - a);
    caseData.documents.stepDownCases = descendingCounts.map((n) => byChildCount.get(n)!);
}

function calcSplitCases(caseData: CaseData): CaseData[] {
    if (caseData.children.custodyArrangement !== 'split') {
        return [];
    }
    const splits = caseData.children.splits ?? [];
    if (splits.length === 0) return [];

    const arrangements = ['shared', 'partyA', 'partyB'] as const;
    type Arrangement = (typeof arrangements)[number];
    // Order by first appearance (oldest child first); each entry = that child's arrangement + all others matching it
    const arrangementsInOrder: Arrangement[] = [];
    for (const s of splits) {
        const c = s?.custodyArrangement;
        if (arrangements.includes(c as Arrangement) && !arrangementsInOrder.includes(c as Arrangement)) {
            arrangementsInOrder.push(c as Arrangement);
        }
    }

    const primaryIndices = arrangementsInOrder.map((a, i) => (a === 'partyA' || a === 'partyB') ? i : -1).filter((i) => i >= 0);
    const otherArrangementIndex = primaryIndices.length >= 2 ? primaryIndices[1] : -1;
    return arrangementsInOrder.map((arrangement, index) => {
        const count = splits.filter((s) => s?.custodyArrangement === arrangement).length;
        const firstSplitWithArrangement = splits.find((s) => s?.custodyArrangement === arrangement);
        const clone = structuredClone(caseData);
        clone.caseType = 'splitCase';
        clone.children.custodyArrangement = arrangement;
        clone.children.count = count;
        clone.children.splits = undefined;
        if (firstSplitWithArrangement?.overnightsPerYear != null && firstSplitWithArrangement.overnightsPerYear !== '') {
            clone.children.overnightsPerYear = firstSplitWithArrangement.overnightsPerYear;
        } else {
            clone.children.overnightsPerYear = undefined;
        }
        if (index === otherArrangementIndex) {
            clone.children.childcareAddOnApplies = caseData.children?.childcareAddOnAppliesSplit;
            clone.children.annualChildcareExpenses = caseData.children?.annualChildcareExpensesSplit;
            clone.children.annualThirdPartyReimbursements = caseData.children?.annualThirdPartyReimbursementsSplit;
            clone.children.numberOfChildrenEligibleForChildcare = caseData.children?.numberOfChildrenEligibleForChildcareSplit;
        }
        clone.documents = {
            summary: undefined,
            worksheet1: undefined,
            stepDownCases: [],
            splitCases: [],
            alternatingEvenCase: undefined,
        };
        return clone;
    });
}

/** Find stepDown entry for a given childrenCount (split uses childrenCount, not array index). */
function getStepDownEntryForCount(party: { tax?: { stepDowns?: StepDownModel[] } } | null, childrenCount: number): StepDownModel | null {
    const stepDowns = party?.tax?.stepDowns;
    if (!stepDowns?.length) return null;
    return stepDowns.find((sd) => (sd?.childrenCount ?? 0) === childrenCount) ?? null;
}

/** Copy stepDown all/odd/even into claimSchedule; normalize null age fields to 0. Preserve schoolDistrict from party. */
function applyStepDownClaimScheduleFromEntry(
    party: { tax?: { claimSchedule?: { all?: YearInfoModel; odd?: YearInfoModel; even?: YearInfoModel; schoolDistrict?: unknown } } } | null,
    stepDown: StepDownModel | null,
    schoolDistrict: unknown
): void {
    if (!party?.tax?.claimSchedule || !stepDown) return;
    const norm = (y: YearInfoModel | null | undefined): YearInfoModel => ({
        age5Under: y?.age5Under ?? 0,
        age6to16: y?.age6to16 ?? 0,
        age17Over: y?.age17Over ?? 0,
        status: y?.status,
        additional: y?.additional,
    });
    party.tax.claimSchedule.all = norm(stepDown.all);
    party.tax.claimSchedule.odd = stepDown.odd ? norm(stepDown.odd) : { ...party.tax.claimSchedule.all };
    party.tax.claimSchedule.even = stepDown.even ? norm(stepDown.even) : { ...party.tax.claimSchedule.all };
    party.tax.claimSchedule.schoolDistrict = schoolDistrict;
}

/** Set party claimSchedule to zero claimed (when no stepDown entry for this k). */
function setClaimScheduleToZero(party: { tax?: { claimSchedule?: { all?: YearInfoModel; odd?: YearInfoModel; even?: YearInfoModel } } } | null): void {
    if (!party?.tax?.claimSchedule) return;
    const zero: YearInfoModel = { age5Under: 0, age6to16: 0, age17Over: 0 };
    const all = party.tax.claimSchedule.all;
    party.tax.claimSchedule.all = { ...zero, status: all?.status };
    party.tax.claimSchedule.odd = { ...zero, status: party.tax.claimSchedule.odd?.status };
    party.tax.claimSchedule.even = { ...zero, status: party.tax.claimSchedule.even?.status };
}

/** For a split case: creates one step-down case per total level n (from totalChildren down to 1) where this arrangement has children. Each clone's claimSchedule is set from root stepDowns keyed by the TOTAL level n (not the per-arrangement count). stepDownTotalLevel tags each case so lookups can match by total level. */
function calcStepDownCasesForSplitCase(splitCase: CaseData, rootCase: CaseData): CaseData[] {
    const arrangement = splitCase.children?.custodyArrangement ?? '';
    const totalChildren = rootCase.children?.count ?? 0;
    const splits = rootCase.children?.splits ?? null;
    if (totalChildren < 1) return [];
    const stepDownCases: CaseData[] = [];
    for (let n = totalChildren; n >= 1; n--) {
        const countsAtN = getStepDownCountsPerArrangement(splits, [], n, totalChildren);
        const countAtN = countsAtN[arrangement] ?? 0;
        if (countAtN <= 0) continue;
        const clone = structuredClone(splitCase);
        clone.caseType = 'stepDownCase';
        clone.stepDownTotalLevel = n;
        clone.children = { ...clone.children!, count: countAtN };
        clone.documents = {
            summary: undefined,
            worksheet1: undefined,
            stepDownCases: [],
            splitCases: [],
            alternatingEvenCase: undefined,
        };
        if (clone.parties?.partyA?.tax) {
            clone.parties.partyA.tax.stepDowns = [];
        }
        if (clone.parties?.partyB?.tax) {
            clone.parties.partyB.tax.stepDowns = [];
        }
        if (n < totalChildren) {
            const stepDownA = getStepDownEntryForCount(rootCase.parties?.partyA ?? null, n);
            const stepDownB = getStepDownEntryForCount(rootCase.parties?.partyB ?? null, n);
            const rootA = rootCase.parties?.partyA;
            const rootB = rootCase.parties?.partyB;
            if (clone.parties?.partyA?.tax) {
                if (stepDownA) {
                    applyStepDownClaimScheduleFromEntry(clone.parties.partyA, stepDownA, rootA?.tax?.schoolDistrict);
                } else {
                    setClaimScheduleToZero(clone.parties.partyA);
                }
            }
            if (clone.parties?.partyB?.tax) {
                if (stepDownB) {
                    applyStepDownClaimScheduleFromEntry(clone.parties.partyB, stepDownB, rootB?.tax?.schoolDistrict);
                } else {
                    setClaimScheduleToZero(clone.parties.partyB);
                }
            }
            clone.children.taxSchedule = (stepDownA?.schedule ?? stepDownB?.schedule) ?? null;
        }
        stepDownCases.push(clone);
    }
    return stepDownCases;
}

/** Populate split case's section6.section6A from its stepDownCases (so extraordinaryVisitationCredit etc. appear in section 6a). */
/** Populate split case's section6.section6A from its stepDownCases (so extraordinaryVisitationCredit etc. appear in section 6a). Deduplicates by per-arrangement child count (keeps highest total level for each count). */
function populateSection6AFromStepDownCases(splitCase: CaseData): void {
    const stepDownCases = splitCase.documents?.stepDownCases ?? [];
    if (stepDownCases.length === 0) return;
    if (!splitCase.documents?.worksheet1?.section6) return;
    if (!splitCase.documents.worksheet1.section6.section6A) {
        splitCase.documents.worksheet1.section6.section6A = [];
    }
    const seenCounts = new Set<number>();
    for (const stepDownCase of stepDownCases) {
        const childCount = stepDownCase.children?.count ?? 0;
        if (seenCounts.has(childCount)) continue;
        seenCounts.add(childCount);
        const section3 = stepDownCase.documents?.worksheet1?.section3;
        if (!section3) continue;
        const ncpWorksheet = section3.petitioner?.isNoncustodialParent ? section3.petitioner : section3.respondent;
        const isAlternating = stepDownCase.children?.taxSchedule === 'alternating';
        const evenSection6 = isAlternating ? stepDownCase.documents?.worksheet1?.section6 : null;
        splitCase.documents.worksheet1.section6.section6A.push({
            numberOfChildren: childCount,
            ncpBasicSupportObligation: ncpWorksheet?.lineG ?? 0.0,
            healthInsuranceAddOrDeduct: (isAlternating ? evenSection6?.offsetHealthInsuranceAddOrDeduct : ncpWorksheet?.lineI) ?? 0.0,
            extraordinaryVisitationCredit: ncpWorksheet?.lineN ?? 0.0,
            guidelineAmountOfCashMedicalSupport: ncpWorksheet?.finalCashMedicalSupportAmount ?? 0.0,
            guidelineAmountOfChildSupport: (isAlternating ? evenSection6?.offsetChildSupportAmount : ncpWorksheet?.finalChildSupportAmount) ?? 0.0,
        });
    }
}

/** Final child support amount from worksheet section3/section4 (same logic as main path). Use for section6 odd/even so values are correct when summary was overwritten with offset. */
function getFinalChildSupportFromWorksheet(caseData: CaseData | null | undefined): number {
    if (!caseData?.documents?.worksheet1) return 0.0;
    const section3 = caseData.documents.worksheet1.section3;
    const section4 = caseData.documents.worksheet1.section4;
    if (caseData?.children?.custodyArrangement === 'shared') {
        if ((section4?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) return section4?.petitioner?.finalChildSupportAmount ?? 0.0;
        if ((section4?.respondent?.finalChildSupportAmount ?? 0.0) > 0.0) return section4?.respondent?.finalChildSupportAmount ?? 0.0;
    } else {
        if ((section3?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) return section3?.petitioner?.finalChildSupportAmount ?? 0.0;
        if ((section3?.respondent?.finalChildSupportAmount ?? 0.0) > 0.0) return section3?.respondent?.finalChildSupportAmount ?? 0.0;
    }
    return 0.0;
}

function calcEvenCase(caseData: CaseData): CaseData {
    // we need to calculate even years scenario for tax info.
    const clone = structuredClone(caseData);
    clone.caseType = 'alternatingEvenCase';
    calcDocumentsIA2025(clone, true, false);

    const petitionerFirstName = getPetitionerParty(caseData)?.personal?.firstName ?? 'Petitioner';
    const respondentFirstName = getRespondentParty(caseData)?.personal?.firstName ?? 'Respondent';

    // Derive odd/even amounts from section3/section4 (worksheet guideline) so section6 is correct
    // even when summary was previously overwritten with the offset (e.g. after a prior calcEvenCase or load from save).
    const oddYearsChildSupportAmount = getFinalChildSupportFromWorksheet(caseData);
    const oddYearsHealthInsuranceAddOrDeduct = caseData?.documents?.worksheet1?.section3?.petitioner?.lineI ?? 0.0;
    var oddYearsPayor = respondentFirstName;
    if ((caseData?.documents?.worksheet1?.section3?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) {
        oddYearsPayor = petitionerFirstName;
    }
    if (caseData?.children?.custodyArrangement === 'shared') {
        if ((caseData?.documents?.worksheet1?.section4?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) {
            oddYearsPayor = petitionerFirstName;
        }
    }

    const evenYearsChildSupportAmount = getFinalChildSupportFromWorksheet(clone);
    const evenYearsHealthInsuranceAddOrDeduct = clone?.documents?.worksheet1?.section3?.petitioner?.lineI ?? 0.0;
    var evenYearsPayor = respondentFirstName;
    if ((clone?.documents?.worksheet1?.section3?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) {
        evenYearsPayor = petitionerFirstName;
    }
    if (clone?.children?.custodyArrangement === 'shared') {
        if ((clone?.documents?.worksheet1?.section4?.petitioner?.finalChildSupportAmount ?? 0.0) > 0.0) {
            evenYearsPayor = petitionerFirstName;
        }
    }

    var offsetChildSupportAmount = roundTo((oddYearsChildSupportAmount + evenYearsChildSupportAmount) / 2.0, 2);
    const offsetHealthInsuranceAddOrDeduct = roundTo((oddYearsHealthInsuranceAddOrDeduct + evenYearsHealthInsuranceAddOrDeduct) / 2.0, 2);
    var offsetPayor = oddYearsPayor;

    if (oddYearsPayor !== evenYearsPayor) {
        offsetChildSupportAmount = roundTo(Math.abs(evenYearsChildSupportAmount - oddYearsChildSupportAmount), 2);
        if (evenYearsChildSupportAmount > oddYearsChildSupportAmount) {
            offsetPayor = evenYearsPayor;
        }
    }

    if (!!caseData?.documents?.worksheet1?.section6) {
        caseData.documents.worksheet1.section6 = {
            ...caseData.documents.worksheet1.section6,
            oddYearsChildSupportAmount: oddYearsChildSupportAmount,
            oddYearsHealthInsuranceAddOrDeduct: oddYearsHealthInsuranceAddOrDeduct,
            oddYearsPayor: oddYearsPayor,
            oddYearsPayee: (oddYearsPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName),

            evenYearsChildSupportAmount: evenYearsChildSupportAmount,
            evenYearsHealthInsuranceAddOrDeduct: evenYearsHealthInsuranceAddOrDeduct,
            evenYearsPayor: evenYearsPayor,
            evenYearsPayee: (evenYearsPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName),

            offsetChildSupportAmount: offsetChildSupportAmount,
            offsetHealthInsuranceAddOrDeduct: offsetHealthInsuranceAddOrDeduct,
            offsetPayor: offsetPayor,
            offsetPayee: (offsetPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName)

        };
    }


    if (!!clone?.documents?.worksheet1?.section6) {
        clone.documents.worksheet1.section6 = {
            ...clone.documents.worksheet1.section6,
            oddYearsChildSupportAmount: oddYearsChildSupportAmount,
            oddYearsHealthInsuranceAddOrDeduct: oddYearsHealthInsuranceAddOrDeduct,
            oddYearsPayor: oddYearsPayor,
            oddYearsPayee: (oddYearsPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName),

            evenYearsChildSupportAmount: evenYearsChildSupportAmount,
            evenYearsHealthInsuranceAddOrDeduct: evenYearsHealthInsuranceAddOrDeduct,
            evenYearsPayor: evenYearsPayor,
            evenYearsPayee: (evenYearsPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName),

            offsetChildSupportAmount: offsetChildSupportAmount,
            offsetHealthInsuranceAddOrDeduct: offsetHealthInsuranceAddOrDeduct,
            offsetPayor: offsetPayor,
            offsetPayee: (offsetPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName)

        };
    }

    if (!!caseData.documents.summary) {
        caseData.documents.summary.finalChildSupport = offsetChildSupportAmount;
        caseData.documents.summary.finalPayor = offsetPayor;
        caseData.documents.summary.finalPayee = (offsetPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName);
    }


    if (!!clone.documents.summary) {
        clone.documents.summary.finalChildSupport = offsetChildSupportAmount;
        clone.documents.summary.finalPayor = offsetPayor;
        clone.documents.summary.finalPayee = (offsetPayor === petitionerFirstName ? respondentFirstName : petitionerFirstName);
    }

    return clone;

}

function calcStepDowns(caseData: CaseData): CaseData[] {

    // ok, now -- we need to calculate stepdowns. this is essentially an enitrely new calculation
    // for each step down using the data from step down as though it's the "actual" values.
    // so we're going to clone the case, overwrite the actual values with stepdowns, run the calculation again,
    // then copy the results into the 

    const rv: CaseData[] = [];

    // debugLog('caseData.children.count', caseData.children.count);

    const partyA = caseData?.parties?.partyA;
    const partyB = caseData?.parties?.partyB;

    const partyAStepDowns: StepDownModel[] = partyA?.tax?.stepDowns ?? [];
    const partyBStepDowns: StepDownModel[] = partyB?.tax?.stepDowns ?? [];

    // Use the maximum length to ensure we process all step-downs from both parties
    const maxLength = Math.max(partyAStepDowns.length, partyBStepDowns.length);
    // debugLog('maxLength', maxLength);

    for (var i = 0; i < maxLength; i++) {
        const stepdownA = partyAStepDowns[i];
        const stepdownB = partyBStepDowns[i];

        // Skip if neither party has a step-down at this index
        if (!stepdownA && !stepdownB) {
            continue;
        }

        // Create a deep copy to avoid mutating the original caseData
        const clone = structuredClone(caseData);
        clone.caseType = 'stepDownCase';

        // debugLog('stepdown[i].childrenCount', stepdownA?.childrenCount, stepdownB?.childrenCount);

        // Use whichever step-down exists (they should have the same childrenCount and schedule)
        const activeStepDown = stepdownA || stepdownB;
        if (activeStepDown) {
            clone.children.taxSchedule = activeStepDown.schedule;
            clone.children.count = activeStepDown.childrenCount;
        }

        if (!!partyA && !!stepdownA) {

            clone.parties!.partyA!.deductions!.isRequestingVariance = false;
            clone.parties!.partyA!.deductions!.variancePayment = 0.0;
            clone.parties!.partyA!.deductions!.yearlyChildCareExpenses = 0.0;
            clone.parties!.partyA!.deductions!.childrenInChildCareCount = 0;
            clone.parties!.partyA!.deductions!.childCareReimbursements = 0.0;

            clone.parties!.partyA!.tax!.stepDowns = []; // wipe all these out so we don't recurse in calcDocuments() call
            clone.parties!.partyA!.tax!.claimSchedule = {
                all: structuredClone(stepdownA.all),
                even: structuredClone(stepdownA.even),
                odd: structuredClone(stepdownA.odd),
                schoolDistrict: structuredClone(partyA?.tax?.schoolDistrict)
            };
        }

        if (!!partyB && !!stepdownB) {

            clone.parties!.partyB!.deductions!.isRequestingVariance = false;
            clone.parties!.partyB!.deductions!.variancePayment = 0.0;
            clone.parties!.partyB!.deductions!.yearlyChildCareExpenses = 0.0;
            clone.parties!.partyB!.deductions!.childrenInChildCareCount = 0;
            clone.parties!.partyB!.deductions!.childCareReimbursements = 0.0;

            clone.parties!.partyB!.tax!.stepDowns = []; // wipe all these out so we don't recurse in calcDocuments() call
            clone.parties!.partyB!.tax!.claimSchedule = {
                all: structuredClone(stepdownB.all),
                even: structuredClone(stepdownB.even),
                odd: structuredClone(stepdownB.odd),
                schoolDistrict: structuredClone(partyB?.tax?.schoolDistrict)
            }
        }

        calcDocumentsIA2025(clone, false, true);

        if (!caseData.documents.worksheet1?.section6) {
            caseData.documents.worksheet1!.section6 = {
                section6A: [],
                section6B: [],
            };
        }

        if (caseData.children.custodyArrangement !== "shared") {
            if (!caseData.documents.worksheet1?.section6.section6A) {
                caseData.documents.worksheet1!.section6.section6A = [];
            }

            const alternating = clone?.children?.taxSchedule === 'alternating';
            const evenSection6 = (alternating ? clone?.documents.worksheet1?.section6 : null);

            const ncpWorksheet = clone.documents.worksheet1?.section3.petitioner.isNoncustodialParent ? clone.documents.worksheet1?.section3.petitioner : clone.documents.worksheet1?.section3.respondent;
            caseData.documents.worksheet1?.section6.section6A.push({
                numberOfChildren: clone.children.count ?? 0,
                ncpBasicSupportObligation: ncpWorksheet?.lineG ?? 0.0,
                healthInsuranceAddOrDeduct: (alternating ? evenSection6?.offsetHealthInsuranceAddOrDeduct : ncpWorksheet?.lineI) ?? 0.0,
                extraordinaryVisitationCredit: ncpWorksheet?.lineN ?? 0.0,
                guidelineAmountOfCashMedicalSupport: ncpWorksheet?.finalCashMedicalSupportAmount ?? 0.0,
                guidelineAmountOfChildSupport: (alternating ? evenSection6?.offsetChildSupportAmount : ncpWorksheet?.finalChildSupportAmount) ?? 0.0
            });
        } else {
            if (!caseData.documents.worksheet1?.section6.section6B) {
                caseData.documents.worksheet1!.section6.section6B = [];
            }
            const petitionerWorksheet = clone.documents.worksheet1?.section4?.petitioner;
            const respondentWorksheet = clone.documents.worksheet1?.section4?.respondent;
            caseData.documents.worksheet1?.section6.section6B.push({
                numberOfChildren: clone.children.count ?? 0,
                petitionerFinalChildSupport: petitionerWorksheet?.finalChildSupportAmount ?? 0.0,
                respondentFinalChildSupport: respondentWorksheet?.finalChildSupportAmount ?? 0.0,
                netFinalChildSupport: Math.abs((petitionerWorksheet?.finalChildSupportAmount ?? 0.0) - (respondentWorksheet?.finalChildSupportAmount ?? 0.0))
            });
        }

        rv.push(clone);

    }

    return rv;
}
